import React, { useCallback, useState } from "react";
import { getPricing } from "../api/kundliApi";
import PaymentModal from "../components/PaymentModal";
import LoginModal from "../components/LoginModal";
import { usePaidActions } from "./usePaidActions";
import { isLoggedIn } from "../utils/authStorage";

/**
 * usePremiumGate("chart", "Janam Kundli Report")
 * -> { runGated(fn), gateModal, priceError }
 *
 * runGated(fn):
 *  1. If already paid for this action this session -> fn() immediately.
 *  2. If not logged in -> show LoginModal first (wallet APIs require auth).
 *  3. Fetch the live price from the backend, show PaymentModal.
 *  4. Once payment is verified -> mark paid, run fn().
 */
export function usePremiumGate(actionKey, title) {
  const { isPaid, markPaid } = usePaidActions();
  const [stage, setStage] = useState(null); // "login" | "payment" | null
  const [price, setPrice] = useState(null);
  const [pendingFn, setPendingFn] = useState(null);
  const [priceError, setPriceError] = useState(null);

  const openPaymentStage = useCallback(async (fn) => {
    try {
      const res = await getPricing();
      setPrice(res.data?.[actionKey]);
      setPendingFn(() => fn);
      setStage("payment");
    } catch (err) {
      setPriceError(err.message || "Couldn't load pricing. Please try again.");
    }
  }, [actionKey]);

  const runGated = useCallback(
    (fn) => {
      setPriceError(null);
      if (isPaid(actionKey)) {
        fn();
        return;
      }
      if (!isLoggedIn()) {
        setPendingFn(() => fn);
        setStage("login");
        return;
      }
      openPaymentStage(fn);
    },
    [actionKey, isPaid, openPaymentStage]
  );

  const gateModal = (
    <>
      {stage === "login" && (
        <LoginModal
          onClose={() => setStage(null)}
          onSuccess={() => openPaymentStage(pendingFn)}
        />
      )}
      {stage === "payment" && (
        <PaymentModal
          title={title}
          price={price}
          onClose={() => setStage(null)}
          onSuccess={() => {
            markPaid(actionKey);
            const fn = pendingFn;
            setStage(null);
            fn();
          }}
        />
      )}
    </>
  );

  return { runGated, gateModal, priceError };
}
